export interface BlogPost {
  slug: string;
  title: string;
  excerpt: string;
  content: string;
  featured_image: string | null;
  featured_image_alt: string | null;
  featured_image_credit: string | null;
  published_at: string;
  category: string | null;
  seo_title: string | null;
  seo_description: string | null;
  seo_keywords: string | null;
  reading_time: number;
}

export interface BlogCategory {
  slug: string;
  name: string;
  count: number;
}

const modules = import.meta.glob('/content/blog/*.md', {
  eager: true,
  query: '?raw',
  import: 'default',
}) as Record<string, string>;

/** Parse YAML frontmatter without gray-matter (which needs Node Buffer) */
function parseFrontmatter(raw: string): { data: Record<string, string>; content: string } {
  const match = raw.match(/^---\r?\n([\s\S]*?)\r?\n---\r?\n([\s\S]*)$/);
  if (!match) return { data: {}, content: raw };

  const yamlBlock = match[1];
  const content = match[2];
  const data: Record<string, string> = {};

  for (const line of yamlBlock.split('\n')) {
    const idx = line.indexOf(':');
    if (idx === -1) continue;
    const key = line.slice(0, idx).trim();
    let value = line.slice(idx + 1).trim();
    // Strip surrounding quotes
    if ((value.startsWith('"') && value.endsWith('"')) || (value.startsWith("'") && value.endsWith("'"))) {
      value = value.slice(1, -1);
    }
    data[key] = value;
  }

  return { data, content };
}

function parsePost(raw: string, filepath: string): BlogPost {
  const { data, content } = parseFrontmatter(raw);

  const slug =
    data.slug ||
    filepath
      .split('/')
      .pop()!
      .replace(/\.md$/, '');

  const wordCount = content.trim().split(/\s+/).length;
  const reading_time = Math.max(1, Math.ceil(wordCount / 200));

  return {
    slug,
    title: data.title || slug,
    excerpt: data.excerpt || '',
    content,
    featured_image: data.featured_image || null,
    featured_image_alt: data.featured_image_alt || null,
    featured_image_credit: data.featured_image_credit || null,
    published_at: data.published_at || new Date().toISOString(),
    category: data.category || null,
    seo_title: data.seo_title || null,
    seo_description: data.seo_description || null,
    seo_keywords: data.seo_keywords || null,
    reading_time,
  };
}

let _posts: BlogPost[] | null = null;

function loadPosts(): BlogPost[] {
  if (_posts) return _posts;
  _posts = Object.entries(modules)
    .map(([filepath, raw]) => parsePost(raw, filepath))
    .sort(
      (a, b) =>
        new Date(b.published_at).getTime() - new Date(a.published_at).getTime(),
    );
  return _posts;
}

export function getAllPosts(): BlogPost[] {
  return loadPosts();
}

export function getPostBySlug(slug: string): BlogPost | undefined {
  return loadPosts().find((p) => p.slug === slug);
}

export function getAllSlugs(): string[] {
  return loadPosts().map((p) => p.slug);
}

function formatCategoryName(slug: string): string {
  return slug
    .split('-')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

export function getAllCategories(): BlogCategory[] {
  const counts: Record<string, number> = {};
  for (const post of loadPosts()) {
    if (post.category) {
      counts[post.category] = (counts[post.category] || 0) + 1;
    }
  }
  return Object.entries(counts)
    .map(([slug, count]) => ({
      slug,
      name: formatCategoryName(slug),
      count,
    }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

export function getRelatedPosts(
  currentSlug: string,
  category: string | null,
  limit = 3,
): BlogPost[] {
  const posts = loadPosts().filter((p) => p.slug !== currentSlug);

  if (category) {
    const sameCat = posts.filter((p) => p.category === category);
    if (sameCat.length >= limit) return sameCat.slice(0, limit);
    if (sameCat.length > 0) {
      const rest = posts.filter((p) => p.category !== category);
      return [...sameCat, ...rest].slice(0, limit);
    }
  }

  return posts.slice(0, limit);
}
